/**
 * MOTIF BAR
 * Barre horizontale des motifs, avec recherche tolerante aux accents et aux fautes.
 *
 * La section collection est entierement remplacee a chaque filtrage (Section Rendering API),
 * donc l'element est reconstruit a chaque fois : la saisie en cours est conservee dans une
 * variable de module et restauree par connectedCallback.
 */
(() => {
  if (window.customElements.get('motif-bar')) {
    return;
  }

  const deburr = (value) =>
    String(value)
      .toLowerCase()
      .replace(/œ/g, 'oe')
      .replace(/æ/g, 'ae')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  /** Distance de Levenshtein, abandonnee des qu'elle depasse max. */
  const distance = (a, b, max) => {
    if (Math.abs(a.length - b.length) > max) {
      return max + 1;
    }

    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);

    for (let i = 1; i <= a.length; i++) {
      const current = [i];
      let best = i;

      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);

        if (current[j] < best) {
          best = current[j];
        }
      }

      if (best > max) {
        return max + 1;
      }

      previous = current;
    }

    return previous[b.length];
  };

  let lastQuery = '';

  class MotifBar extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-motif-search]');
      this.scroller = this.querySelector('[data-motif-scroller]');
      this.rail = this.querySelector('[data-motif-rail]');
      this.prevButton = this.querySelector('[data-motif-prev]');
      this.nextButton = this.querySelector('[data-motif-next]');
      this.emptyMessage = this.querySelector('[data-motif-empty]');
      this.entries = Array.from(this.querySelectorAll('[data-motif]')).map((chip) => {
        const label = deburr(chip.dataset.motif);

        return { chip, label, words: label.split(' ').filter(Boolean) };
      });

      this._measure = this._measure.bind(this);
      this._onInput = this._onInput.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onSearchClick = this._onSearchClick.bind(this);
      this._onBlur = this._onBlur.bind(this);
      this._syncArrows = this._syncArrows.bind(this);
      this._onPrev = () => this._nudge(-1);
      this._onNext = () => this._nudge(1);
      this.expanded = false;
      this.stuck = false;

      this._measure();

      if (this.scroller) {
        this.scroller.addEventListener('scroll', this._syncArrows, { passive: true });
      }

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this._onPrev);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this._onNext);
      }

      this._syncArrows();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this._measure();
          this._syncArrows();
        });
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this._measure);
        window.addEventListener('resize', this._syncArrows);
      }

      this._scrollActiveIntoView();

      if (!this.input) {
        return;
      }

      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('keydown', this._onKeydown);
      this.input.addEventListener('blur', this._onBlur);

      this.searchBox = this.querySelector('.motif-bar__search');

      if (this.searchBox) {
        this.searchBox.addEventListener('click', this._onSearchClick);
      }

      // Le mode compact n'a de sens que si la barre est collante.
      if (getComputedStyle(this).position === 'sticky') {
        window.addEventListener('scroll', this._onScroll, { passive: true });
        this._onScroll();
      }

      if (lastQuery) {
        this.input.value = lastQuery;
        this._apply(lastQuery);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this._measure);
        window.removeEventListener('resize', this._syncArrows);
      }

      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this._syncArrows);
      }

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this._onPrev);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this._onNext);
      }

      if (!this.input) {
        return;
      }

      this.input.removeEventListener('input', this._onInput);
      this.input.removeEventListener('keydown', this._onKeydown);
      this.input.removeEventListener('blur', this._onBlur);
      window.removeEventListener('scroll', this._onScroll);

      if (this.searchBox) {
        this.searchBox.removeEventListener('click', this._onSearchClick);
      }
    }

    /**
     * La barre est-elle collee en haut ? Si oui, on replie la recherche.
     * Deux lectures de mise en page par evenement, aucune ecriture tant que l'etat ne change
     * pas : volontairement sans requestAnimationFrame, qui se met en pause quand l'onglet n'est
     * pas peint et laissait un verrou bloque.
     */
    _onScroll() {
      const limit = parseFloat(getComputedStyle(this).top) || 0;
      const stuck = this.getBoundingClientRect().top <= limit + 1;

      // Filet de secours : si la recherche a ete ouverte puis laissee vide, un nouveau
      // defilement la referme, meme si l'evenement blur n'est jamais arrive.
      const busy =
        this.input && (document.activeElement === this.input || this.input.value.trim() !== '');
      const expanded = this.expanded && Boolean(busy);

      if (stuck === this.stuck && expanded === this.expanded) {
        return;
      }

      this.stuck = stuck;
      this.expanded = expanded;
      this._syncCompact();
    }

    _syncCompact() {
      const compact = this.stuck && !this.expanded;

      if (compact === this.hasAttribute('compact')) {
        return;
      }

      this.toggleAttribute('compact', compact);
      this._measure();
    }

    _onSearchClick() {
      if (!this.hasAttribute('compact')) {
        return;
      }

      this.expanded = true;
      this._syncCompact();
      this.input.focus();
    }

    _onBlur() {
      if (this.input.value.trim() !== '') {
        return;
      }

      this.expanded = false;
      this._syncCompact();
    }

    /** Fait glisser la rangee de pastilles d'un ecran, moins un chevauchement. */
    _nudge(direction) {
      if (!this.scroller) {
        return;
      }

      this.scroller.scrollBy({ left: direction * this.scroller.clientWidth * 0.8, behavior: 'smooth' });
    }

    /** Montre les fleches et les degrades uniquement du cote ou il reste des motifs. */
    _syncArrows() {
      if (!this.scroller) {
        return;
      }

      const maximum = this.scroller.scrollWidth - this.scroller.clientWidth;
      const canPrev = this.scroller.scrollLeft > 4;
      const canNext = this.scroller.scrollLeft < maximum - 4;

      if (this.rail) {
        this.rail.toggleAttribute('data-can-prev', canPrev);
        this.rail.toggleAttribute('data-can-next', canNext);
      }

      if (this.prevButton) {
        this.prevButton.hidden = !canPrev;
      }

      if (this.nextButton) {
        this.nextButton.hidden = !canNext;
      }
    }

    /**
     * Publie la hauteur reelle de la barre. Sur la racine, pour que la colonne de filtres
     * collante et le recadrage automatique apres filtrage la prennent tous les deux en compte.
     */
    _measure() {
      document.documentElement.style.setProperty(
        '--motif-bar-height',
        `${Math.round(this.offsetHeight)}px`
      );
    }

    _onInput() {
      lastQuery = this.input.value;
      this._apply(lastQuery);
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.input.value = '';
        lastQuery = '';
        this._apply('');
        this.input.blur();

        return;
      }

      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();

      const visible = this.entries.filter((entry) => !entry.chip.hidden);

      if (visible.length === 1) {
        visible[0].chip.click();
      }
    }

    _apply(rawQuery) {
      const query = deburr(rawQuery);

      if (!query) {
        this.entries.forEach((entry) => {
          entry.chip.hidden = false;
        });

        if (this.emptyMessage) {
          this.emptyMessage.hidden = true;
        }

        if (this.scroller) {
          this.scroller.hidden = false;
        }

        this.removeAttribute('filtering');
        this._measure();
        this._syncArrows();

        return;
      }

      const terms = query.split(' ').filter(Boolean);
      let matches = this.entries.filter((entry) => terms.every((term) => entry.label.includes(term)));

      // Rien d'exact : on retombe sur les motifs les plus proches.
      if (matches.length === 0) {
        const tolerance = query.length >= 5 ? 2 : 1;

        matches = this.entries.filter(
          (entry) =>
            entry.words.some((word) => distance(query, word, tolerance) <= tolerance) ||
            distance(query, entry.label, tolerance) <= tolerance
        );
      }

      const matched = new Set(matches.map((entry) => entry.chip));

      this.entries.forEach((entry) => {
        entry.chip.hidden = !matched.has(entry.chip);
      });

      if (this.emptyMessage) {
        this.emptyMessage.hidden = matches.length > 0;
      }

      this.setAttribute('filtering', '');

      if (this.scroller) {
        // Masquer la rangee evite que le bouton "Tous les motifs" chevauche le message.
        this.scroller.hidden = matches.length === 0;
        this.scroller.scrollLeft = 0;
      }

      this._measure();
      this._syncArrows();
    }

    _scrollActiveIntoView() {
      const active = this.querySelector('[data-motif][aria-current="true"]');

      if (!active || !this.scroller) {
        return;
      }

      this.scroller.scrollLeft = Math.max(
        0,
        active.offsetLeft - this.scroller.clientWidth / 2 + active.clientWidth / 2
      );

      this._syncArrows();
    }
  }

  window.customElements.define('motif-bar', MotifBar);
})();